import {
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import { FiDownload } from 'react-icons/fi';
import LoadingLottie from '../components/atoms/LoadingLottie';
import useGetParams from '../hooks/useGetParams';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import {
  useGetEventOrdersReportQuery,
} from '../store';
import AdminSectionTitle from '../components/AdminSectionTitle';
import AssistanceReportTab from '../components/AssistanceReportTab';
import SellersReportTab from '../components/SellersReportTab';
import GeneralReportTab from '../components/GeneralReportTab';

const PDF_EXPORT_WIDTH_PX = 1500;

const EventSalesReportPdfPage: React.FC = () => {
  const { eventId, businessId } = useGetParams();
  const { event } = useGetSelectedEvent();
  const navigate = useNavigate();
  const { data: reportData, isLoading } = useGetEventOrdersReportQuery({
    businessId,
    eventId,
  });
  const contentRef = useRef<HTMLDivElement>(null);
  const autoGenerationTriggeredRef = useRef(false);

  const handleExportPdf = useCallback(async () => {
    if (!contentRef.current) {
      return;
    }

    try {
      // Hide sidebar and other elements that shouldn't be in PDF
      const sidebar = document.querySelector('[class*="MuiDrawer"]') as HTMLElement | null;
      const breadcrumbsElement = document.querySelector('[class*="breadcrumb"]')
        || document.querySelector('nav[aria-label="breadcrumb"]');
      const breadcrumbs = breadcrumbsElement ? (breadcrumbsElement as HTMLElement) : null;
      const footerElement = document.querySelector('footer')
        || document.querySelector('[class*="footer"]');
      const footer = footerElement ? (footerElement as HTMLElement) : null;

      const originalSidebarDisplay = sidebar?.style.display || '';
      const originalBreadcrumbsDisplay = breadcrumbs?.style.display || '';
      const originalFooterDisplay = footer?.style.display || '';
      const exportHiddenElements = Array.from(
        contentRef.current.querySelectorAll('[data-hide-during-export="true"]'),
      ) as HTMLElement[];
      const originalExportDisplays = exportHiddenElements.map((el) => el.style.display || '');

      // Hide elements
      if (sidebar) {
        sidebar.style.display = 'none';
      }
      if (breadcrumbs) {
        breadcrumbs.style.display = 'none';
      }
      if (footer) {
        footer.style.display = 'none';
      }
      for (const element of exportHiddenElements) {
        element.style.display = 'none';
      }

      // Scroll to top to ensure we capture from the beginning
      window.scrollTo(0, 0);
      contentRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });

      // Wait a bit for DOM to update and ensure content is rendered
      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 500);
      });

      // Wait for all images to load
      const images = contentRef.current.querySelectorAll('img');
      const imagePromises = Array.from(images).map((imgElement) => {
        const img = imgElement as HTMLImageElement;
        if (img.complete) {
          return Promise.resolve();
        }
        return new Promise<void>((resolve) => {
          const handleLoad = () => resolve();
          const handleError = () => resolve(); // Continue even if image fails
          img.addEventListener('load', handleLoad, { once: true });
          img.addEventListener('error', handleError, { once: true });
          setTimeout(() => {
            img.removeEventListener('load', handleLoad);
            img.removeEventListener('error', handleError);
            resolve();
          }, 2000); // Timeout after 2 seconds
        });
      });
      await Promise.all(imagePromises);

      // Store original styles to restore later
      const originalContentStyles = {
        width: contentRef.current.style.width,
        maxWidth: contentRef.current.style.maxWidth,
        minWidth: contentRef.current.style.minWidth,
        overflow: contentRef.current.style.overflow,
        backgroundColor: contentRef.current.style.backgroundColor,
        paddingBottom: contentRef.current.style.paddingBottom,
      };

      // Ensure content is fully visible and set a fixed export width
      contentRef.current.style.width = `${PDF_EXPORT_WIDTH_PX}px`;
      contentRef.current.style.maxWidth = `${PDF_EXPORT_WIDTH_PX}px`;
      contentRef.current.style.minWidth = `${PDF_EXPORT_WIDTH_PX}px`;
      contentRef.current.style.overflow = 'visible';
      contentRef.current.style.backgroundColor = 'white';
      // Add padding at bottom to prevent cutoff (increased from 40px)
      contentRef.current.style.paddingBottom = '100px';

      // Add data attribute to force desktop layout during PDF export
      contentRef.current.setAttribute('data-pdf-export', 'true');

      // Store original styles for grid and flex elements to restore later
      const originalElementStyles = new Map<HTMLElement, { display?: string; gridTemplateColumns?: string; flexDirection?: string }>();

      // Force all responsive grids to use multi-column layout
      const gridElements = contentRef.current.querySelectorAll('[class*="grid-cols"]');
      gridElements.forEach((el) => {
        const htmlEl = el as HTMLElement;
        const classes = htmlEl.className;

        // Store original styles before modifying
        if (!originalElementStyles.has(htmlEl)) {
          originalElementStyles.set(htmlEl, {
            display: htmlEl.style.display || '',
            gridTemplateColumns: htmlEl.style.gridTemplateColumns || '',
          });
        }

        // Prioritize larger breakpoints (lg over md)
        if (classes.includes('lg:grid-cols-3')) {
          htmlEl.style.display = 'grid';
          htmlEl.style.gridTemplateColumns = 'repeat(3, minmax(0, 1fr))';
        } else if (classes.includes('md:grid-cols-2')) {
          htmlEl.style.display = 'grid';
          htmlEl.style.gridTemplateColumns = 'repeat(2, minmax(0, 1fr))';
        }
      });

      // Force flex-row layout for elements with lg:flex-row
      const flexElements = contentRef.current.querySelectorAll('[class*="flex-col"]');
      flexElements.forEach((el) => {
        const htmlEl = el as HTMLElement;
        const classes = htmlEl.className;

        // Only process if it has lg:flex-row (desktop should be row)
        if (classes.includes('lg:flex-row')) {
          // Store original styles before modifying
          if (!originalElementStyles.has(htmlEl)) {
            originalElementStyles.set(htmlEl, {
              flexDirection: htmlEl.style.flexDirection || '',
            });
          } else {
            // Update existing entry if needed
            const existing = originalElementStyles.get(htmlEl);
            if (existing) {
              existing.flexDirection = htmlEl.style.flexDirection || '';
            }
          }

          // Force row layout
          htmlEl.style.flexDirection = 'row';
        }
      });

      // Force ResponsiveContainer charts to recalculate by triggering resize
      // Wait for styles to apply and charts to recalculate
      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 500);
      });

      // Trigger window resize event to force ResponsiveContainer to recalculate
      window.dispatchEvent(new Event('resize'));

      // Wait for charts to recalculate after resize
      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 300);
      });

      // Ensure all chart containers have proper width and are centered
      // Target ResponsiveContainer wrappers and chart parent divs, but EXCLUDE pie charts
      const chartContainers = contentRef.current.querySelectorAll('.recharts-wrapper, [style*="height: 300"], [style*="height:300"], svg.recharts-surface');
      chartContainers.forEach((container) => {
        const htmlEl = container as HTMLElement;

        // Check if this is a pie chart by looking for Pie component elements
        // Pie charts have specific structure with Pie elements inside
        const isPieChart = htmlEl.querySelector('g.recharts-pie') !== null
          || htmlEl.closest('[class*="PieChart"]') !== null
          || (htmlEl.querySelector('g')?.querySelector('path[class*="recharts-pie"]') !== null);

        // Skip pie charts - don't apply recentering to them as it breaks the legend
        if (isPieChart) {
          return;
        }

        // Ensure container has proper width for centering (only for non-pie charts)
        if (!htmlEl.style.width || htmlEl.style.width === '0px') {
          htmlEl.style.width = '100%';
        }
        // Ensure parent containers are properly sized
        const parent = htmlEl.parentElement;
        if (parent) {
          if (!parent.style.width || parent.style.width === '0px') {
            parent.style.width = '100%';
          }
          // Ensure parent has display block/flex for proper centering
          const computedStyle = window.getComputedStyle(parent);
          if (computedStyle.display === 'inline' || computedStyle.display === 'inline-block') {
            parent.style.display = 'block';
          }
        }
      });

      // Wait for SVG elements to render (SVGs render synchronously but need time for layout)
      // Just wait a bit for all SVG charts to finish rendering
      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 200);
      });

      // Wait one more time for all layout calculations to complete
      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 200);
      });

      // Configure PDF page to match full content size so it becomes a single sheet
      const pxToMm = (px: number) => (px * 25.4) / 96;
      const contentWidthPx = PDF_EXPORT_WIDTH_PX;
      // Recalculate height after all changes - use scrollHeight which includes padding
      // Add 20% extra height to make the page taller and ensure all content fits
      const baseHeight = Math.max(
        contentRef.current.scrollHeight,
        contentRef.current.offsetHeight,
      );
      const contentHeightPx = Math.ceil(baseHeight * 1.2); // 20% taller
      const contentWidthMm = pxToMm(contentWidthPx);
      const contentHeightMm = pxToMm(contentHeightPx);
      const orientation: 'landscape' | 'portrait' = contentWidthMm > contentHeightMm ? 'landscape' : 'portrait';
      const margin: [number, number, number, number] = [0, 0, 0, 0];
      const pdfFormat: [number, number] = [
        contentWidthMm + margin[1] + margin[3],
        contentHeightMm + margin[0] + margin[2],
      ];
      const opt = {
        margin,
        filename: `${event?.name || 'Reporte'}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
          width: contentWidthPx,
          height: contentHeightPx,
          scrollX: 0,
          scrollY: 0,
          windowWidth: PDF_EXPORT_WIDTH_PX,
          windowHeight: contentHeightPx,
          allowTaint: true,
        },
        jsPDF: {
          unit: 'mm',
          format: pdfFormat,
          orientation,
          compress: true,
        },
      };

      // Generate PDF directly from the content element
      await html2pdf().set(opt).from(contentRef.current).save();

      // Remove PDF export data attribute
      contentRef.current.removeAttribute('data-pdf-export');

      // Restore original grid and flex styles
      const allModifiedElements = contentRef.current.querySelectorAll('[class*="grid-cols"], [class*="flex-col"]');
      allModifiedElements.forEach((el) => {
        const htmlEl = el as HTMLElement;
        const originalStyles = originalElementStyles.get(htmlEl);
        if (originalStyles) {
          if (originalStyles.display !== undefined) {
            htmlEl.style.display = originalStyles.display;
          }
          if (originalStyles.gridTemplateColumns !== undefined) {
            htmlEl.style.gridTemplateColumns = originalStyles.gridTemplateColumns;
          }
          if (originalStyles.flexDirection !== undefined) {
            htmlEl.style.flexDirection = originalStyles.flexDirection;
          }
        }
      });

      // Restore original content styles
      contentRef.current.style.width = originalContentStyles.width;
      contentRef.current.style.maxWidth = originalContentStyles.maxWidth;
      contentRef.current.style.minWidth = originalContentStyles.minWidth;
      contentRef.current.style.overflow = originalContentStyles.overflow;
      contentRef.current.style.backgroundColor = originalContentStyles.backgroundColor;
      contentRef.current.style.paddingBottom = originalContentStyles.paddingBottom;

      // Restore original display styles
      if (sidebar) {
        sidebar.style.display = originalSidebarDisplay;
      }
      if (breadcrumbs) {
        breadcrumbs.style.display = originalBreadcrumbsDisplay;
      }
      if (footer) {
        footer.style.display = originalFooterDisplay;
      }
      for (let i = 0; i < exportHiddenElements.length; i += 1) {
        exportHiddenElements[i].style.display = originalExportDisplays[i];
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setTimeout(() => {
        navigate('/');
      }, 1000);
    }
  }, [businessId, eventId, event?.name, navigate]);

  useEffect(() => {
    if (isLoading || !reportData || autoGenerationTriggeredRef.current) {
      return;
    }
    autoGenerationTriggeredRef.current = true;
    setTimeout(() => {
      handleExportPdf();
    }, 5000);
  }, [isLoading, reportData, handleExportPdf]);

  if (isLoading) {
    return <LoadingLottie />;
  }
  if (!reportData) {
    return <div>No hay datos</div>;
  }

  return (
    <>
      <div className="flex flex-col gap-8 my-6 w-full min-w-[1500px] max-w-[1500px] px-6 mx-auto" ref={contentRef}>
        <div className="flex flex-row justify-between items-center" data-hide-during-export="true">
          <AdminSectionTitle
            title={'Eventos'}
            subtitle={`${event?.name} - Reportes`}
            mainButtons={[{
              label: 'Exportar PDF',
              onClick: handleExportPdf,
              variant: 'secondary',
              icon: <FiDownload className='text-xl' />,
            }]}
          />
        </div>

        <h1 className="text-2xl font-bold text-text-primary">{event?.name}</h1>

        {/* Datos generales section */}
        <div className="flex flex-col gap-4">
          <h2 className="text-2xl font-semibold text-text-primary">Datos generales</h2>
          <GeneralReportTab event={event} hideAiSummary />
        </div>

        {/* Tickets y asistencia section */}
        <div className="flex flex-col gap-4">
          <h2 className="text-2xl font-semibold text-text-primary">Tickets y asistencia</h2>
          <AssistanceReportTab event={event} hideAiSummary />
        </div>

        {/* Vendedores section */}
        <div className="flex flex-col gap-4">
          <h2 className="text-2xl font-semibold text-text-primary">Vendedores</h2>
          <SellersReportTab event={event} hideAiSummary />
        </div>
      </div>

      <div
        className="fixed inset-0 flex items-center justify-center bg-surface-base bg-opacity-70 backdrop-blur-sm z-50 w-full"
        data-hide-during-export="true"
      >
        <div className="flex flex-col items-center gap-4 glass-surface p-6 rounded-xl shadow-lg w-full">
          <LoadingLottie />
        </div>
      </div>
    </>
  );
};

export default EventSalesReportPdfPage;
